import React, { useCallback, useState } from 'react';

import {
  arrayOf, bool, func, number, objectOf, oneOfType, string,
} from 'prop-types';
import cn from 'classnames';

import Logger from '../../../services/Logger';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import { formatProductAttributeTitle } from '../../../store/helpers/productAttributes';
import Separator from '../../details/view/CustomFields/Separator';
import ProductField from './ProductField';
import styles from './ProductMetaFields.module.scss';

export default function ProductCustomFieldGroup({
  data,
  metaDiff,
  detailsPanelVisibility,
  customFieldsAllowed,
  toggleVisibility,
  disabled,
  readOnly,
  readOnlyTooltip,
  onChange,
  inProgressItems,
  group,
}) {
  const [errors, setErrors] = useState({});

  const handleValidationChange = useCallback((fieldTitle, error) => {
    setErrors((prev) => ({ ...prev, [fieldTitle]: error }));
  }, []);

  const handleChange = useCallback((value, field, onCancel = Function.prototype, { isAttach } = {}) => {
    const {
      title, type, multiple, visibility,
    } = field;
    Logger.log('User', 'ProductInfoChangeCustomField', title);

    let focusedInput = null;
    setTimeout(() => { focusedInput = document.querySelector('input:focus'); }, 0);

    onChange({
      title,
      type,
      value: type === 'enum' && value === null ? '' : value,
      multiple,
      isAttach,
      onCancel,
      visibility: visibility || 'visible',
    });

    if (focusedInput) focusedInput.focus();
  }, [onChange]);

  if (group.isTmp) return null;

  const separator = group[0];
  const isGroupHidden = separator.visibility === 'hidden';

  if (isGroupHidden) return null;

  const isVisible = Boolean(detailsPanelVisibility[separator.title]);
  const groupToRender = customFieldsAllowed && isVisible ? group : [separator];

  return (
    <div
      data-qa="details-component-customFields"
      className={cn('detailsPanel__customFields', styles.group, { act: isVisible })}
      data-block="detailsCustomFields"
    >
      <div
        key={separator.title}
        data-qa={`details-component-customFields-${separator.title}`}
        className={cn('customFieldGroup')}
      >
        {groupToRender.map((field) => {
          if (field.type === 'separator') {
            return (
              <Separator
                key={field.title}
                title={field.title}
                toggleVisibility={toggleVisibility}
                customFieldsAllowed={customFieldsAllowed}
              />
            );
          }
          const displayField = {
            ...field,
            title: formatProductAttributeTitle(field.title),
          };

          return (
            <ReadOnlyFieldTooltip
              key={field.title}
              tooltip={readOnly && readOnlyTooltip ? readOnlyTooltip : null}
            >
              <ProductField
                data={displayField}
                value={data[field.title]}
                readOnly={readOnly}
                disabled={disabled || (field.default && !field.writable)}
                isDiff={(metaDiff || {})[field.title] ?? false}
                inProgress={inProgressItems.includes(field.title)}
                savedError={errors[field.title] ?? null}
                onValidationChange={(error) => handleValidationChange(field.title, error)}
                onChange={(value, _field, onCancel, settings) => (
                  handleChange(value, field, onCancel, settings)
                )}
              />
            </ReadOnlyFieldTooltip>
          );
        })}
      </div>
    </div>
  );
}

ProductCustomFieldGroup.defaultProps = {
  metaDiff: {},
  onChange: Function.prototype,
  disabled: false,
  readOnly: false,
  readOnlyTooltip: null,
  inProgressItems: [],
  customFieldsAllowed: true,
  group: [],
};

ProductCustomFieldGroup.propTypes = {
  data: objectOf(oneOfType([bool, string, number, arrayOf(string)])).isRequired,
  metaDiff: objectOf(bool),
  detailsPanelVisibility: objectOf(bool).isRequired,
  inProgressItems: arrayOf(string),
  disabled: bool,
  readOnly: bool,
  readOnlyTooltip: string,
  customFieldsAllowed: bool,
  group: arrayOf(objectOf(oneOfType([bool, string, number, arrayOf(string)]))),
  toggleVisibility: func.isRequired,
  onChange: func,
};
